// src/views/litellm/trends/TrendsView.jsx
import React, { useMemo, useState } from "react";
import dayjs from "dayjs";
import { HeaderPortal } from "../../../components/ui-components/HeaderSlot.jsx";
import AnalyzerPeriodSelector from "../../cost-analyzer/AnalyzerPeriodSelector.jsx";
import FilterDropdown from "../../../components/ui-components/FilterDropdown";
import { useAiTrends } from "./hooks";
import { BREAKDOWNS, METRICS, breakdownById } from "./trendsConfig";
import TrendsKpiStrip from "./TrendsKpiStrip";
import ConsumptionChart from "./ConsumptionChart";
import ContributorsTable from "./ContributorsTable";
import MoversPanel from "./MoversPanel";
import EconomicsChart from "./EconomicsChart";

// AI Usage → Trends. Historical AI consumption analysis, deliberately built to
// the same interaction pattern as Cost & Usage so the user learns ONE analysis
// model: what happened → where → why → economic impact.
//
// Default period is the last 30 days rather than the current month: it is
// always a full window, so the prior-period comparison is always like-for-like
// regardless of where in the month the user lands.
//
// Deliberately no add-filter control here, unlike /cost-analyzer. AddFilterControl's
// field list comes from the analyzer's options (service_name + four ownership
// perspective codes), but the trends endpoint only accepts four metered cost-base
// columns and silently drops anything else — five of those fields would look
// functional and do nothing, and its value picker reads the whole cost base, so
// even the accepted fields would offer non-AI values. Shipping it would be worse
// than omitting it, so `filters` is left out of scope entirely rather than passed
// as an always-empty array.
const EMPTY = { kpis: {}, series: [], contributors: [], movers: [], economics: [], window: {} };

const Divider = () => <span aria-hidden style={{ width: 1, height: 16 }} className="bg-card-bd" />;

export default function TrendsView() {
  const [startDate, setStartDate] = useState(() => dayjs().subtract(29, "day").startOf("day"));
  const [endDate, setEndDate] = useState(() => dayjs().endOf("day"));
  const [breakdown, setBreakdown] = useState("model");
  const [metric, setMetric] = useState("tokens");
  const [comparison, setComparison] = useState("prior_period");

  const scope = useMemo(
    () => ({
      from: startDate.format("YYYY-MM-DD"),
      to: endDate.format("YYYY-MM-DD"),
      breakdown,
      comparison,
    }),
    [startDate, endDate, breakdown, comparison]
  );

  const query = useAiTrends(scope);
  const data = query.data || EMPTY;
  const dim = breakdownById(breakdown);

  return (
    <>
      <HeaderPortal slot="row2-right">
        <div className="flex items-center gap-1">
          <AnalyzerPeriodSelector
            startDate={startDate}
            endDate={endDate}
            setStartDate={setStartDate}
            setEndDate={setEndDate}
            variant="tertiary"
          />
          <Divider />
          <FilterDropdown
            value={comparison}
            onChange={setComparison}
            options={[
              { value: "none", label: "No comparison" },
              { value: "prior_period", label: "vs prior period" },
              { value: "prior_year", label: "vs last year" },
            ]}
            ariaLabel="Comparison"
            minWidth={150}
            height={32}
            variant="tertiary"
          />
        </div>
      </HeaderPortal>

      <HeaderPortal slot="row3-filters">
        <div className="flex items-center gap-3">
          <FilterDropdown
            value={breakdown}
            onChange={setBreakdown}
            options={BREAKDOWNS.map((b) => ({ value: b.id, label: b.label }))}
            ariaLabel="Breakdown"
            prefix="Breakdown:"
            minWidth={190}
            height={32}
          />
          <FilterDropdown
            value={metric}
            onChange={setMetric}
            options={METRICS.map((m) => ({ value: m.id, label: m.label }))}
            ariaLabel="Metric"
            prefix="Metric:"
            minWidth={190}
            height={32}
          />
        </div>
      </HeaderPortal>

      <div className="flex flex-col gap-4 p-6">
        {query.error ? (
          <div className="rounded-card bg-danger-soft p-4 text-sm text-danger">
            Failed to load AI trends.{" "}
            <button type="button" className="underline" onClick={() => query.refetch()}>
              Retry
            </button>
          </div>
        ) : (
          <>
            <TrendsKpiStrip kpis={data.kpis} />

            <ConsumptionChart
              series={data.series}
              metric={metric}
              breakdownLabel={dim.label}
              bucket={data.window?.bucket || "day"}
              ownership={Boolean(data.ownership)}
              idleCapacity={data.kpis?.idleCapacity || 0}
            />

            <ContributorsTable
              contributors={data.contributors}
              breakdownLabel={dim.label}
              ownership={Boolean(data.ownership)}
              idleCapacity={data.kpis?.idleCapacity || 0}
              hasComparison={Boolean(data.kpis?.hasComparison)}
            />

            <MoversPanel movers={data.movers} />

            <EconomicsChart economics={data.economics} idleCapacity={data.kpis?.idleCapacity || 0} />
          </>
        )}
      </div>
    </>
  );
}
